// The kit. Factories that build a whole mechanic from a few parameters, for
// the shapes that come up again and again: give a piece an ability, put a
// flag or a timer on one of yours, on one of theirs, or on a random one of
// yours. A mechanic that fits none of these is written out by hand in
// src/spells/mechanics with defineMechanic.
//
// Every factory takes a spec. The spec is rule text, not voice. The card in
// src/content/cards.ts gives it a voice later.

import { addAbilities, addLearnSources } from '../engine/abilities';
import type { Ability, Piece } from '../engine/types';
import {
  comboLine,
  defineMechanic,
  describePiece,
  enemyPieces,
  noEffect,
  ownPieces,
  pieceAt,
  say,
  updatePiece,
} from './helpers';
import type { AiPreference, AiScorer, SpellFamily, SpellMechanic, SpellSpec } from './types';

export const PICK_OWN = 'Pick one of your pieces.';
export const PICK_ENEMY = 'Pick an enemy piece.';

type Lines = SpellSpec['lines'];

const spec = (summary: string, prompt: string, lines: Lines): SpellSpec => ({
  summary,
  prompt,
  lines,
});

/**
 * A buff that gives one of your pieces an ability it lacks.
 * Lines: cast {who} {ability}, castCombo adds {combo}.
 */
export const grantAbility = (
  id: string,
  ability: Ability,
  summary: string,
  lines: Lines,
  family: SpellFamily = 'ability',
): SpellMechanic =>
  defineMechanic({
    id,
    polarity: 'ally',
    family,
    targetCount: 1,
    spec: spec(summary, PICK_OWN, lines),
    eligible: (state, caster) =>
      ownPieces(
        state,
        caster,
        (piece) => !piece.abilities.includes(ability) && piece.kind !== 'dormant',
      ),
    cast: ({ state, self }, [square]) => {
      if (square === undefined) return state;
      const piece = pieceAt(state, square);
      const next = updatePiece(state, square, {
        abilities: addAbilities(piece.abilities, [ability]),
        gained: addAbilities(piece.gained, [ability]),
        learnedFrom: addLearnSources(piece.learnedFrom, [ability], { how: 'spell', spellId: id }),
      });
      const [key, vars] = comboLine(pieceAt(next, square));
      return say(next, self, key, { who: describePiece(piece, square), ability, ...vars });
    },
  });

export type MarkOptions = {
  id: string;
  family: SpellFamily;
  summary: string;
  prompt: string;
  /** Which pieces may be picked. */
  eligible: (piece: Piece) => boolean;
  /** What changes on the piece. */
  patch: Partial<Piece>;
  /** Lines: cast {who}. Add any vars you pass in `vars`. */
  lines: Lines;
  vars?: Record<string, string | number>;
  aiPreference?: AiPreference;
  aiScore?: AiScorer;
};

/** A buff that flags or times one of your pieces. */
export const markOwn = (options: MarkOptions): SpellMechanic =>
  defineMechanic({
    id: options.id,
    polarity: 'ally',
    family: options.family,
    targetCount: 1,
    aiPreference: options.aiPreference ?? 'valuable',
    ...(options.aiScore ? { aiScore: options.aiScore } : {}),
    spec: spec(options.summary, options.prompt, options.lines),
    eligible: (state, caster) => ownPieces(state, caster, options.eligible),
    cast: ({ state, self }, [square]) => {
      if (square === undefined) return state;
      const piece = pieceAt(state, square);
      return say(updatePiece(state, square, options.patch), self, 'cast', {
        who: describePiece(piece, square),
        ...options.vars,
      });
    },
  });

/** A debuff that flags or times one enemy piece. */
export const markEnemy = (options: MarkOptions): SpellMechanic =>
  defineMechanic({
    id: options.id,
    polarity: 'enemy',
    family: options.family,
    targetCount: 1,
    aiPreference: options.aiPreference ?? 'valuable',
    ...(options.aiScore ? { aiScore: options.aiScore } : {}),
    spec: spec(options.summary, options.prompt, options.lines),
    eligible: (state, caster) => enemyPieces(state, caster, options.eligible),
    cast: ({ state, self }, [square]) => {
      if (square === undefined) return state;
      const piece = pieceAt(state, square);
      return say(updatePiece(state, square, options.patch), self, 'cast', {
        who: describePiece(piece, square),
        ...options.vars,
      });
    },
  });

/**
 * A backfire that flags or times a random one of your pieces. Nobody aims.
 * Lines: cast {who}, noEffect.
 */
export const strikeOwn = (options: Omit<MarkOptions, 'prompt'>): SpellMechanic =>
  defineMechanic({
    id: options.id,
    polarity: 'ally',
    family: options.family,
    spec: spec(options.summary, '', options.lines),
    cast: ({ state, caster, rng, self }) => {
      const candidates = ownPieces(state, caster, options.eligible);
      if (candidates.length === 0) return noEffect(state, self);
      const square = rng.pick(candidates);
      const piece = pieceAt(state, square);
      return say(updatePiece(state, square, options.patch), self, 'cast', {
        who: describePiece(piece, square),
        ...options.vars,
      });
    },
  });
